import {Alert, Button, Space, Table, message} from "antd";
import type {ColumnsType} from "antd/es/table";
import {StyledPageHeader} from "../../../../../styles/dashboard/calls/calls";
import {CloseOutlined, DownloadOutlined} from "@ant-design/icons";
import {
  StyledNoUpcomingCallBox,
  StyledUpcomingCallsWrapper,
} from "../../../../calls/upcoming-calls/upcoming-calls.style";
import {useState} from "react";
import {CustomPagination} from "../../../../pagination";
import {StyledSessionDetailsDrawer} from "../../../../calls/service/sales-and-insights/customers-section/style";
import {SessionDetailsDrawerBookingDetails} from "../../../../calls/service/sales-and-insights/customers-section/booking-details";
import {SessionDetailsDrawerBookingQuestions} from "../../../../calls/service/sales-and-insights/customers-section/booking-questions";
import {
  StyledSessionDetailsDrawerBookingDetailsItem,
  StyledSessionDetailsDrawerBookingDetailsWrapper,
} from "../../../../calls/service/sales-and-insights/customers-section/booking-details/style";
import {useGetStoreServiceSalesAndInsightsCustomerTransactionsApi} from "../../../../../react-query-hooks/store/services/get-store-service-sales-and-insights";
import {useExportCustomerPurchases} from "@/features/product-services";
import {FullPageLoader} from "../../../../full-page-loader";
import {StyledDescriptionText} from "../../../../messages/messages.style";
import moment from "moment";

interface DataType {
  key: string;
  name: string;
  email: string;
  reference: string;
  id: number;
  date: string;
  price: string;
  bookingQuestions: {answer?: string | null; question: string}[];
  amount: string;
  currency: string;
}

type StoreServiceCustomerInsightsProps = {
  storeServiceId: number;
  storeServiceTitle: string;
  serviceCreatedAt: string;
};

type StoreServiceCustomerTransactionsBookingDetailsDrawerDetailsState = {
  id: number;
  name: string;
  title: string;
  email: string;
  price: string;
  reference: string;
  date: string;
  bookingQuestions: {answer?: string | null; question: string}[];
  amount: number;
  currency: string;
};

export function StoreServiceCustomerInsights({
  storeServiceId,
  storeServiceTitle,
  serviceCreatedAt,
}: StoreServiceCustomerInsightsProps) {
  const [page, setPage] = useState(1);
  const exportCustomerPurchasesMutation = useExportCustomerPurchases();
  const [
    isStoreServiceCustomerTransactionsBookingDetailsDrawerOpen,
    setIsStoreServiceCustomerTransactionsDetailsDrawerOpen,
  ] = useState(false);
  const [
    productDrawerBookingDetails,
    setStoreServiceCustomerTransactionsDrawerBookingDetails,
  ] = useState<StoreServiceCustomerTransactionsBookingDetailsDrawerDetailsState>();
  const getStoreServiceSalesAndInsightsCustomerTransactionsApi =
    useGetStoreServiceSalesAndInsightsCustomerTransactionsApi({
      storeServiceId: storeServiceId,
      currentPage: page,
    });
  // const exportCsvQueryMutation = useStoreServiceCustomerTransactionsCustomerExportCSV();

  if (getStoreServiceSalesAndInsightsCustomerTransactionsApi.isLoading) {
    return <FullPageLoader />;
  }
  if (getStoreServiceSalesAndInsightsCustomerTransactionsApi.isError) {
    return (
      <Alert
        message="Error"
        description={getStoreServiceSalesAndInsightsCustomerTransactionsApi.error.message}
        type="error"
        showIcon
      />
    );
  }
  console.log({
    getStoreServiceSalesAndInsightsCustomerTransactionsApi:
      getStoreServiceSalesAndInsightsCustomerTransactionsApi,
  });

  const customersData: DataType[] =
    getStoreServiceSalesAndInsightsCustomerTransactionsApi.data.data.customerPurchases.map(
      (customer) => ({
        key: customer.id.toString(),
        name: `${customer.firstName} ${customer.lastName}`,
        email: customer.emailAddress.toLowerCase(),
        reference: customer.reference,
        date: new Date(customer.createdAt).toDateString(),
        id: customer.id,
        price:
          customer.amount === "0.00"
            ? "Free"
            : `${customer.currency} ${customer.amount.toLocaleString()}`,
        bookingQuestions: customer.booking_questions ? customer.booking_questions : [],
        amount: customer.amount,
        currency: customer.currency,
      })
    );

  function showStoreServiceCustomerTransactionsBookingDetailsDrawer(
    values: StoreServiceCustomerTransactionsBookingDetailsDrawerDetailsState
  ) {
    setStoreServiceCustomerTransactionsDrawerBookingDetails(values);
    setIsStoreServiceCustomerTransactionsDetailsDrawerOpen(true);
  }
  function closeStoreServiceCustomerTransactionsBookingDetailsDrawer() {
    setIsStoreServiceCustomerTransactionsDetailsDrawerOpen(false);
  }

  const columns: ColumnsType<DataType> = [
    {
      title: "Name",
      dataIndex: "name",
      fixed: "left",
      width: "17.425rem",
      key: "name",
      render: (text) => (
        <>
          <StyledDescriptionText style={{textTransform: "capitalize"}}>
            {text}
          </StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      width: "17.425rem",
      key: "email",
      render: (text) => (
        <>
          <StyledDescriptionText>{text}</StyledDescriptionText>
        </>
      ),
    },
    {
      title: "Reference",
      dataIndex: "reference",
      width: "17.425rem",
      key: "reference",
      render: (text) => (
        <>
          <StyledDescriptionText>{text}</StyledDescriptionText>
        </>
      ),
    },
    {
      title: "",
      dataIndex: "action",
      width: "17.425rem",
      key: "action",
      render: (text, record) => (
        <>
          <Button
            onClick={() =>
              showStoreServiceCustomerTransactionsBookingDetailsDrawer({
                date: record.date,
                email: record.email,
                id: record.id,
                name: record.name,
                price: record.price,
                reference: record.reference,
                bookingQuestions: record.bookingQuestions,
                amount: +record.amount,
                currency: record.currency,
                title: storeServiceTitle,
              })
            }
            block
            type="text"
            size="small"
          >
            <span style={{textDecoration: "underline"}}>View details</span>
          </Button>
        </>
      ),
    },
  ];

  const handleExportAsCSV = () => {
    if (!storeServiceId) return;

    exportCustomerPurchasesMutation.mutate(
      {
        productServiceId: storeServiceId,
        // No date-picker in the UI — export all-time (service creation -> today).
        startDate: moment(serviceCreatedAt).format("YYYY-MM-DD"),
        endDate: moment().format("YYYY-MM-DD"),
      },
      {
        onSuccess: async (blob) => {
          // An empty range comes back as an application/json message, not a CSV.
          if (blob.type.includes("application/json")) {
            try {
              const parsed = JSON.parse(await blob.text());
              message.info(parsed?.message ?? "No customer purchases to export.");
            } catch {
              message.info("No customer purchases to export.");
            }
            return;
          }

          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "product-service-customers.csv");
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          message.success("File ready to be downloaded!");
        },
        onError: () => {
          message.error("Could not export customer purchases. Please try again.");
        },
      }
    );
  };

  return (
    <>
      <div style={{marginTop: "7rem"}}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
            marginBottom: "4rem",
          }}
        >
          {/* <StyledPageHeader>Customers 📇</StyledPageHeader> */}
          <StyledPageHeader>Customers</StyledPageHeader>
          <div>
            <Button
              onClick={handleExportAsCSV}
              disabled={customersData.length < 1}
              block
              icon={<DownloadOutlined style={{fontSize: "1.6rem"}} />}
              type="text"
              loading={exportCustomerPurchasesMutation.isLoading}
              size="small"
            >
              <span style={{textDecoration: "underline"}}>Export CSV</span>
            </Button>
          </div>
        </div>
        {customersData.length < 1 ? (
          <StyledUpcomingCallsWrapper style={{paddingTop: 0}}>
            <StyledNoUpcomingCallBox>
              <p className="info-text">Your customers will appear here</p>
            </StyledNoUpcomingCallBox>
          </StyledUpcomingCallsWrapper>
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={customersData}
              loading={getStoreServiceSalesAndInsightsCustomerTransactionsApi.isFetching}
              scroll={{x: 100}}
              pagination={false}
            />
            <CustomPagination
              isPreviousData={
                getStoreServiceSalesAndInsightsCustomerTransactionsApi.isPreviousData
              }
              page={page}
              paginationMeta={
                getStoreServiceSalesAndInsightsCustomerTransactionsApi.data.meta
              }
              setPage={setPage}
            />
          </>
        )}
      </div>
      {productDrawerBookingDetails && (
        <StyledSessionDetailsDrawer
          title="Customer details"
          placement="right"
          onClose={closeStoreServiceCustomerTransactionsBookingDetailsDrawer}
          visible={isStoreServiceCustomerTransactionsBookingDetailsDrawerOpen}
          // mask={false}
          closable={false}
          extra={
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={closeStoreServiceCustomerTransactionsBookingDetailsDrawer}
            ></Button>
          }
        >
          <div>
            <Space direction="vertical" style={{display: "flex"}} size={24}>
              <SessionDetailsDrawerBookingDetails
                data={[
                  {
                    title: "Service name",
                    value: productDrawerBookingDetails.title,
                  },
                  {
                    title: "Customer's name",
                    value: productDrawerBookingDetails.name,
                  },
                  {
                    title: "Customer’s email",
                    value: productDrawerBookingDetails.email,
                  },
                  {
                    title: "Date",
                    value: productDrawerBookingDetails.date,
                  },
                  {
                    title: "Price",
                    value: productDrawerBookingDetails.price,
                  },
                  {
                    title: "Reference",
                    value: productDrawerBookingDetails.reference,
                  },
                ]}
              />
              <SessionDetailsDrawerBookingQuestions
                data={productDrawerBookingDetails.bookingQuestions}
              />
            </Space>
          </div>
        </StyledSessionDetailsDrawer>
      )}
    </>
  );
}
